import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HistoryIcon from '@mui/icons-material/History';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LoadingSpinner from './LoadingSpinner';
import SurvivalTable from './SurvivalTable';
import GameModeRulesModal from './GameModeRulesModal';
import groupService from '../services/GroupService';
import { debugError } from '../utils/debugUtils';

const CYCLE_TYPES = ['DAILY', 'WEEKLY', 'MONTHLY'];

export default function SurvivalTab({ groupId, isAdmin, t, onSnackbar }) {
  const [survival, setSurvival] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [cycleType, setCycleType] = useState('DAILY');
  const [autoRestart, setAutoRestart] = useState(false);
  const [starting, setStarting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [viewingId, setViewingId] = useState(null);

  const loadCurrent = useCallback(() => {
    setLoading(true);
    setError(false);
    groupService
      .getCurrentSurvival(groupId)
      .then(setSurvival)
      .catch((err) => {
        if (err.code === 8013) {
          setSurvival(null);
        } else {
          debugError('[SurvivalTab] Error loading survival:', err);
          setError(true);
        }
      })
      .finally(() => setLoading(false));
  }, [groupId]);

  useEffect(() => {
    setViewingId(null);
    loadCurrent();
  }, [loadCurrent]);

  useEffect(() => {
    if (viewingId == null) return;
    setLoading(true);
    groupService
      .getSurvival(groupId, viewingId)
      .then(setSurvival)
      .catch((err) => {
        debugError('[SurvivalTab] Error loading survival detail:', err);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [groupId, viewingId]);

  const handleStart = async () => {
    setStarting(true);
    try {
      const created = await groupService.startSurvival(groupId, cycleType, autoRestart);
      setSurvival(created);
      setStartOpen(false);
      setViewingId(null);
    } catch (err) {
      onSnackbar({ open: true, message: t(`apiErrors.${err.code}`, t('common.error')), severity: 'error' });
    } finally {
      setStarting(false);
    }
  };

  const handleOpenHistory = () => {
    setHistoryOpen(true);
    groupService
      .listSurvivals(groupId)
      .then((data) => setHistory(Array.isArray(data) ? data : []))
      .catch((err) => debugError('[SurvivalTab] Error loading history:', err));
  };

  if (loading) return <LoadingSpinner />;

  const canStart = isAdmin && (!survival || survival.status === 'COMPLETED');

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {viewingId != null && (
            <IconButton size="small" onClick={() => setViewingId(null)}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <Typography variant="subtitle1" fontWeight={700}>
            {t('groups.survival.title')}
          </Typography>
          <IconButton size="small" onClick={() => setRulesOpen(true)} aria-label={t('groups.survival.infoTooltip')}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
        <IconButton size="small" onClick={handleOpenHistory} aria-label={t('groups.survival.historyButton')}>
          <HistoryIcon fontSize="small" />
        </IconButton>
      </Box>

      {error ? (
        <Typography textAlign="center" color="error" py={2}>
          {t('common.error')}
        </Typography>
      ) : !survival ? (
        <Typography textAlign="center" color="text.secondary" py={4}>
          {t('groups.survival.noneActive')}
        </Typography>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Chip
            size="small"
            color={survival.status === 'ACTIVE' ? 'primary' : 'default'}
            label={t(survival.status === 'ACTIVE' ? 'groups.survival.statusActive' : 'groups.survival.statusCompleted')}
            sx={{ mb: 1.5 }}
          />
          {survival.status === 'COMPLETED' && survival.winnerName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <EmojiEventsIcon color="warning" fontSize="small" />
              <Typography variant="body2" fontWeight={700}>
                {t('groups.survival.winner', { name: survival.winnerName })}
              </Typography>
            </Box>
          )}
          <SurvivalTable participants={survival.participants} t={t} />
        </Box>
      )}

      {canStart && viewingId == null && (
        <Button variant="contained" onClick={() => setStartOpen(true)} sx={{ mt: 1 }}>
          {t('groups.survival.startButton')}
        </Button>
      )}

      <Dialog open={startOpen} onClose={() => setStartOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <strong>{t('groups.survival.startConfirmTitle')}</strong>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('groups.survival.startConfirmMessage')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('groups.survival.cycleLabel')}
          </Typography>
          <Select
            value={cycleType}
            onChange={(e) => setCycleType(e.target.value)}
            size="small"
            fullWidth
            sx={{ mt: 0.5, mb: 1.5 }}
          >
            {CYCLE_TYPES.map((c) => (
              <MenuItem key={c} value={c}>
                {t(`groups.survival.cycle${c.charAt(0)}${c.slice(1).toLowerCase()}`)}
              </MenuItem>
            ))}
          </Select>
          <FormControlLabel
            control={<Checkbox checked={autoRestart} onChange={(e) => setAutoRestart(e.target.checked)} />}
            label={t('groups.survival.autoRestartLabel')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartOpen(false)} disabled={starting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleStart} variant="contained" disabled={starting}>
            {t('groups.survival.startButton')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <strong>{t('groups.survival.historyTitle')}</strong>
        </DialogTitle>
        <DialogContent>
          {history.length === 0 ? (
            <Typography variant="body2" color="text.secondary" py={2}>
              {t('groups.survival.historyEmpty')}
            </Typography>
          ) : (
            <List>
              {history.map((h) => (
                <ListItemButton
                  key={h.id}
                  onClick={() => {
                    setViewingId(h.id);
                    setHistoryOpen(false);
                  }}
                >
                  <ListItemText
                    primary={h.winnerName ? t('groups.survival.winner', { name: h.winnerName }) : t(`groups.survival.status${h.status === 'ACTIVE' ? 'Active' : 'Completed'}`)}
                    secondary={h.startedAt ? new Date(h.startedAt).toLocaleDateString() : ''}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <GameModeRulesModal open={rulesOpen} onClose={() => setRulesOpen(false)} mode="survival" t={t} />
    </Box>
  );
}
